import { Link } from 'react-router-dom';
import { Bell, CheckCheck } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/dropdown-menu';
import { formatRelative } from '@/lib/format';
import { paths } from '@/routes/paths';
import { cn } from '@/lib/cn';
import {
  useListNotificationsQuery,
  useMarkAllReadMutation,
  useMarkNotificationReadMutation,
  useUnreadCountQuery,
} from './api';
import { CATEGORY_LABEL } from './labels';

/**
 * The bell.
 *
 * The count is an `aria-live` region, so a screen-reader user hears it change rather
 * than only seeing a number appear. It is also never computed on the client: every
 * mutation returns the authoritative number and the stream pushes it, so two open tabs
 * always agree.
 */
export function NotificationBell() {
  const { data: count } = useUnreadCountQuery();
  const unread = count?.data.unreadCount ?? 0;

  // Only the most recent few — the full list is a page of its own. A popover that
  // paginates is a page in the wrong place.
  const { data, isFetching } = useListNotificationsQuery({ page: 1, pageSize: 6 });
  const [markRead] = useMarkNotificationReadMutation();
  const [markAllRead, { isLoading: markingAll }] = useMarkAllReadMutation();

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="icon" className="relative">
          <Bell aria-hidden className="size-4" />
          {unread > 0 && (
            <span
              aria-hidden
              className={cn(
                'absolute -right-0.5 -top-0.5 grid min-w-4 place-items-center rounded-full',
                'bg-danger-fill px-1 text-[10px] font-semibold leading-4 text-white',
              )}
            >
              {unread > 99 ? '99+' : unread}
            </span>
          )}
          <span className="sr-only">
            Notifications{unread > 0 ? `, ${unread} unread` : ''}
          </span>
        </Button>
      </PopoverTrigger>

      {/* Outside the popover so it is announced whether or not the panel is open. */}
      <span aria-live="polite" aria-atomic="true" className="sr-only">
        {unread > 0 ? `${unread} unread notifications` : 'No unread notifications'}
      </span>

      <PopoverContent align="end" className="w-[22rem] p-0">
        <div className="flex items-center justify-between border-b border-line px-3 py-2">
          <h2 className="text-body font-semibold">Notifications</h2>
          {unread > 0 && (
            <Button
              variant="ghost"
              size="sm"
              loading={markingAll}
              onClick={() => void markAllRead()}
            >
              <CheckCheck aria-hidden className="size-3.5" />
              Mark all read
            </Button>
          )}
        </div>

        <div className="max-h-[22rem] overflow-y-auto">
          {isFetching && !data ? (
            <p className="px-3 py-8 text-center text-caption text-fg-muted">Loading…</p>
          ) : (data?.data.length ?? 0) === 0 ? (
            <p className="px-3 py-8 text-center text-caption text-fg-muted">
              Nothing yet. You will hear about approvals, tasks and attendance here.
            </p>
          ) : (
            <ul className="divide-y divide-line">
              {data?.data.map((notification) => (
                <li key={notification.id}>
                  <Link
                    to={notification.redirectUrl ?? paths.notifications}
                    onClick={() => {
                      if (!notification.read) void markRead(notification.id);
                    }}
                    className={cn(
                      'block px-3 py-2.5 hover:bg-hover',
                      !notification.read && 'bg-selected/40',
                    )}
                  >
                    <p className="flex items-start gap-2 text-body font-medium">
                      {!notification.read && (
                        <span
                          aria-hidden
                          className="mt-1.5 size-1.5 shrink-0 rounded-full bg-action"
                        />
                      )}
                      <span className="min-w-0 flex-1">{notification.title}</span>
                    </p>
                    <p className="pl-3.5 text-caption text-fg-muted">{notification.body}</p>
                    <p className="pl-3.5 text-caption text-fg-subtle">
                      {CATEGORY_LABEL[notification.category as keyof typeof CATEGORY_LABEL] ??
                        notification.category}{' '}
                      ·{' '}
                      {formatRelative(notification.createdAt)}
                    </p>
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="border-t border-line px-3 py-2 text-center">
          <Link to={paths.notifications} className="text-caption text-link hover:underline">
            See all notifications
          </Link>
        </div>
      </PopoverContent>
    </Popover>
  );
}
